import { useEffect, useState } from 'react'
import axios, { endpoints } from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { fDateTime } from '@/utils/format-date'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { LogLive } from './log.types'

type Props = {
  channelName: string
}

export default function LogLiveTable({ channelName }: Props) {
  const [logs, setLogs] = useState<LogLive[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(false)

  const fetchLogs = async (p: number) => {
    setLoading(true)
    try {
      const { data } = await axios.get(endpoints.programs.logLives, {
        params: { page: p, limit: 10, channelName },
      })

      setLogs(data.items)
      setTotalPages(data.totalPages || 1)
      setPage(data.page)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLogs(1)
  }, [channelName])

  const handleNextPage = () => {
    if (page < totalPages) {
      fetchLogs(page + 1)
    }
  }

  const handlePreviousPage = () => {
    if (page > 1) {
      fetchLogs(page - 1)
    }
  }

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Event Type</TableHead>
            <TableHead>Message</TableHead>
            <TableHead>Timestamp</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={3} className="text-center">
                Loading...
              </TableCell>
            </TableRow>
          ) : (
            logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>{log.eventType}</TableCell>
                <TableCell>
                  <pre className="whitespace-pre-wrap">
                    {JSON.stringify(log.payload, null, 2)}
                  </pre>
                </TableCell>
                <TableCell>{fDateTime(log.createdAt)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <div className="flex items-center justify-end space-x-2 py-4">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePreviousPage}
          disabled={page <= 1 || loading}
        >
          Previous
        </Button>
        <span className="text-sm">
          Page {page} of {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={handleNextPage}
          disabled={page >= totalPages || loading}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
